import Flags from "common/constants/flags";

declare global {
  interface Window {
    higharcGpuUnavailableOverride?: boolean;
  }
}

export function isSafari(): boolean {
  return !!(
    navigator.vendor &&
    navigator.vendor.indexOf("Apple") > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf("Chrome") === -1 &&
    navigator.userAgent.indexOf("Firefox") === -1
  );
}

export enum OS {
  MAC,
  IPAD,
  IPHONE,
  WINDOWS,
  ANDROID,
  LINUX,
}

/**
 * @returns True if running on node.js (as opposed to the browser)
 */
export function isOnNodeJS(): boolean {
  // typeof window === "undefined" doesn't work since some tests define it.
  return typeof process !== "undefined" && process?.release?.name === "node";
}

/**
 * @returns True if running inside Lambda.
 */
export function isOnLambda(): boolean {
  return !!process.env.LAMBDA_TASK_ROOT;
}

/**
 * @returns True if running inside Jest.
 */
export function isJestTest(): boolean {
  return process.env.JEST_WORKER_ID !== undefined;
}

/**
 * Suitable for mitigating platform-specific bugs or performance differences. Avoid using for feature detection.
 * @returns Best guess of the user's OS.
 */
export function getOS(): OS | undefined {
  // Based on: https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
  if (typeof window === "undefined") {
    return undefined;
  }

  const userAgent = window.navigator.userAgent;
  // userAgentData is a new API, type definitions are not up to date yet.
  const navigatorPlatform = (window.navigator as any)?.userAgentData?.platform ?? window.navigator.platform;

  const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iPadPlatforms = ["iPad"];
  const iPhonePlatforms = ["iPhone", "iPod"];

  if (macosPlatforms.indexOf(navigatorPlatform) !== -1) {
    if (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) {
      return OS.IPAD; //iPad Pro
    }
    return OS.MAC;
  }
  if (iPadPlatforms.indexOf(navigatorPlatform) !== -1) {
    return OS.IPAD;
  }
  if (
    iPhonePlatforms.indexOf(navigatorPlatform) !== -1 ||
    userAgent.includes("iPhone") /* The user agent check is needed for Chrome dev tools mobile emulation */
  ) {
    return OS.IPHONE;
  }
  if (windowsPlatforms.indexOf(navigatorPlatform) !== -1) {
    return OS.WINDOWS;
  }
  if (/Android/i.test(userAgent)) {
    return OS.ANDROID;
  }
  if (/Linux/i.test(navigatorPlatform)) {
    return OS.LINUX;
  }
  return undefined;
}

export function getGPUDebugRendererInfo(gl: WebGLRenderingContext | WebGL2RenderingContext) {
  let gpuVendor = "Unknown";
  let gpuRendererInfo = "Unknown";
  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  if (debugInfo) {
    gpuVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    gpuRendererInfo = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
  }
  return { gpuVendor, gpuRendererInfo };
}

export function disableWebgl2TextureAtlas(gpuVendor: string) {
  // WebGL 2 texture atlas performs badly on Linux / NVIDIA.
  return getOS() === OS.LINUX && gpuVendor.toLowerCase().includes("nvidia");
}

// This should only return true in test environments
export function gpuUnavailable(): boolean {
  if (typeof window !== "undefined" && "higharcGpuUnavailableOverride" in window) {
    return window.higharcGpuUnavailableOverride === true;
  }

  return process.env.PUBLIC_GPU_UNAVAILABLE === "true";
}

// This is useful when trying to develop offline
export function bypassCognito(): boolean {
  // In dev serve the frontend reads a window flag injected by vite.config's dev plugin, because
  // Vite's `define` doesn't reach workspace-package source in dev. Prod builds and the server
  // both fall through to the `define`d / real `process.env` value.
  if (typeof window !== "undefined" && (window as { higharcBypassCognito?: boolean }).higharcBypassCognito) {
    return true;
  }
  return process.env.PUBLIC_BYPASS_COGNITO === "true";
}

export function isLocalhost(): boolean {
  return !isOnNodeJS() && window.location.hostname === "localhost";
}

export function isSentryEnabled(): boolean {
  return !Flags.IS_DEV_MODE && !Flags.TEST_E2E_MODE && !isLocalhost();
}

export function isRenderDebugEnabled(): boolean {
  return Flags.RENDER_DEBUG && Flags.IS_DEV_MODE;
}
